import { UI_CONSTANTS } from '@/constants';
import { useDialogFocus } from '@/hooks/use-dialog-focus';
import { trackEvent } from '@/utils/analytics';
import React, { useEffect } from 'react';

interface AboutModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export const AboutModal: React.FC<AboutModalProps> = ({ isOpen, onClose }) => {
    const dialogRef = useDialogFocus(isOpen, onClose);
    // Track modal opened
    useEffect(() => {
        if (isOpen) {
            trackEvent('modal_opened', {
                modal_name: 'about',
            });
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div
            className={`fixed inset-0 bg-backdrop backdrop-blur-sm flex items-center justify-center overflow-hidden`}
            style={{ zIndex: UI_CONSTANTS.Z_INDEX.MODAL }}
            onClick={handleBackdropClick}
        >
            <div
                ref={dialogRef}
                role='dialog'
                aria-modal='true'
                aria-labelledby='about-title'
                tabIndex={-1}
                className='bg-vscode-dark border border-vscode rounded-lg shadow-xl max-w-md w-full mx-4'
            >
                <div className='p-4'>
                    <div className='flex items-center justify-between mb-4'>
                        <h2 id='about-title' className='text-lg font-semibold text-vscode-primary'>
                            About
                        </h2>
                        <button
                            type='button'
                            aria-label='Close about dialog'
                            onClick={onClose}
                            className='text-vscode-secondary hover:text-vscode-primary transition-colors'
                        >
                            ✕
                        </button>
                    </div>
                    <hr className='my-4 border-vscode' />

                    <div className='text-vscode-primary text-sm leading-relaxed'>
                        <p className='mb-3'>
                            This is a portfolio website for Faris Rehman that mimics the Visual Studio Code IDE
                            experience.
                        </p>

                        <p className='mb-3 font-medium text-vscode-secondary'>Key Technologies:</p>
                        <ul className='space-y-1 text-vscode-secondary ml-4'>
                            <li>• React 19 with TypeScript</li>
                            <li>• Vite for build tooling</li>
                            <li>• TailwindCSS v4 for styling</li>
                            <li>• @szhsin/react-menu for context menus</li>
                        </ul>

                        <p className='mt-4 text-xs text-vscode-muted'>Designed and built by Faris Rehman</p>
                    </div>

                    <div className='flex justify-end mt-6'>
                        <button
                            onClick={onClose}
                            className='px-4 py-2 bg-vscode-hover hover:bg-vscode-hover-light text-vscode-primary rounded transition-colors'
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
